import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "results", "selectedList", "template"]
  static values = {
    users: Array,
    mode: String, // "roles" or "notifications"
    defaultRole: { type: String, default: "Assessor" }
  }

  connect() {
    this.selectedUserIds = new Set()
    this.initializeExistingSelections()
  }

  initializeExistingSelections() {
    // Collect IDs of already selected users from existing form fields
    const existingInputs = this.selectedListTarget.querySelectorAll('input[name*="[user_id]"]')
    existingInputs.forEach(input => {
      if (input.value) {
        this.selectedUserIds.add(parseInt(input.value))
      }
    })
  }

  search(event) {
    const query = event.target.value.trim().toLowerCase()

    if (query.length === 0) {
      this.clearResults()
      return
    }

    // Simple substring search
    const matches = this.usersValue.filter(user => {
      const name = user.name.toLowerCase()
      const email = user.email.toLowerCase()
      const organisation = (user.organisation || '').toLowerCase()
      return (name.includes(query) || email.includes(query) || organisation.includes(query)) &&
             !this.selectedUserIds.has(user.id)
    })

    this.renderResults(matches)
  }

  clearResults(event) {
    // Use setTimeout to allow click events on dropdown items to fire first
    setTimeout(() => {
      this.resultsTarget.innerHTML = ''
      this.resultsTarget.classList.remove('show')
    }, 200)
  }

  renderResults(users) {
    if (users.length === 0) {
      this.resultsTarget.innerHTML = '<div class="dropdown-item text-muted">No users found</div>'
      this.resultsTarget.classList.add('show')
      return
    }

    this.resultsTarget.innerHTML = users.map(user => {
      const displayName = user.organisation ? `${user.name} (${user.organisation})` : user.name
      return `
        <button type="button"
                class="dropdown-item text-dark"
                data-action="click->user-selector#selectUser"
                data-user-id="${user.id}"
                data-user-name="${user.name}"
                data-user-organisation="${user.organisation || ''}"
                data-user-email="${user.email}">
          ${displayName}
        </button>
      `
    }).join('')

    this.resultsTarget.classList.add('show')
  }

  selectUser(event) {
    const userId = parseInt(event.currentTarget.dataset.userId)
    const userName = event.currentTarget.dataset.userName
    const userOrganisation = event.currentTarget.dataset.userOrganisation
    const userEmail = event.currentTarget.dataset.userEmail

    // Add to selected set
    this.selectedUserIds.add(userId)

    // Clone template and populate
    const template = this.templateTarget.content.cloneNode(true)
    const container = template.querySelector('[data-user-container]')

    // Set unique timestamp for Rails nested attributes
    const timestamp = new Date().getTime()

    // Update all name attributes in the template
    const inputs = template.querySelectorAll('input, select')
    inputs.forEach(input => {
      if (input.name) {
        input.name = input.name.replace(/NEW_RECORD/g, timestamp)
      }
    })

    // Set user ID
    const userIdInput = template.querySelector('[data-field="user-id"]')
    if (userIdInput) {
      userIdInput.value = userId
    }

    // Set default role if in roles mode
    if (this.modeValue === 'roles') {
      const roleSelect = template.querySelector('[data-field="role"]')
      if (roleSelect) {
        roleSelect.value = this.defaultRoleValue.toLowerCase()
      }
    }

    // Display user name
    const nameSpan = template.querySelector('[data-field="user-name"]')
    if (nameSpan) {
      const displayName = userOrganisation ? `${userName} (${userOrganisation})` : userName
      nameSpan.textContent = displayName
    }

    // Set user ID on container for removal
    if (container) {
      container.dataset.userId = userId
    }

    // Add to selected list
    this.selectedListTarget.appendChild(template)

    // Clear search
    this.searchInputTarget.value = ''
    this.resultsTarget.innerHTML = ''
    this.resultsTarget.classList.remove('show')
  }

  removeUser(event) {
    const container = event.target.closest('[data-user-container]')
    const userId = parseInt(container.dataset.userId)

    // Check if we're in notifications mode (simple array) or roles mode (nested attributes)
    if (this.modeValue === 'notifications') {
      // Notifications mode - just remove the container
      container.remove()
    } else {
      // Roles mode - check if this is an existing record (has an ID) or new record
      const idInput = container.querySelector('input[name*="[id]"]')

      if (idInput && idInput.value) {
        // Existing record - mark for destruction
        const destroyInput = container.querySelector('input[name*="[_destroy]"]')
        if (destroyInput) {
          destroyInput.value = '1'
        }
        container.style.display = 'none'
      } else {
        // New record - just remove from DOM
        container.remove()
      }
    }

    // Remove from selected set
    this.selectedUserIds.delete(userId)
  }

  // Close dropdown when clicking outside
  disconnect() {
    this.resultsTarget.classList.remove('show')
  }
}
